/**
 * Состояние замка экрана: снят или нет.
 *
 * Живёт вне React, потому что читателей двое и они в разных ветках дерева:
 * `PinGate` (показывает экран ввода) и кнопки-замочки в шапках экранов.
 *
 * Источник истины — переменная в памяти, `sessionStorage` только зеркалит её,
 * чтобы замок не спрашивали заново при перезагрузке внутри той же сессии.
 * Наоборот делать нельзя: в некоторых WebView storage недоступен и бросает, и
 * тогда «снято» никогда бы не записалось — приложение осталось бы заперто
 * навсегда, даже с верным пином.
 */

const UNLOCKED_KEY = 'sg.pin.unlocked';

/** Подписчику значение не передаём: он перечитывает его через `isUnlocked`. */
type Listener = () => void;

const listeners = new Set<Listener>();

function readStorage(): boolean {
  try {
    return sessionStorage.getItem(UNLOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStorage(unlocked: boolean): void {
  try {
    if (unlocked) sessionStorage.setItem(UNLOCKED_KEY, '1');
    else sessionStorage.removeItem(UNLOCKED_KEY);
  } catch {
    /* no-op: память остаётся источником истины */
  }
}

let unlocked = readStorage();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Снапшот для `useSyncExternalStore`: стабильная ссылка, без объектов. */
export function isUnlocked(): boolean {
  return unlocked;
}

/** Пин принят: снять замок до закрытия приложения. */
export function markUnlocked(): void {
  unlocked = true;
  writeStorage(true);
  notify();
}

/** Заблокировать вручную — кнопка-замочек в шапке. */
export function lockApp(): void {
  unlocked = false;
  writeStorage(false);
  notify();
}

export function subscribeLock(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
